import axios from '../utils/axios';
import { getDateInTimeZone, isInReportingRange } from '../utils/reportingDates';
import { formatSupportError } from '../utils/supportTickets';
import { getSupportCreatedCount } from './supportService';

const PAGE_SIZE = 100;
const MAX_RECORDS = 50000;
const countOf = (value) => Number.isSafeInteger(value) && value >= 0 ? value : null;
const idOf = (value) => {
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  const id = value?._id ?? value?.id;
  return id == null || id === '' ? null : String(id);
};
const unavailable = (reason, total = null, observedAt = null) => Object.assign(new Error(reason), { total, observedAt });

// Date filters are not part of these existing list contracts. Visit every page
// and refuse to report a partial collection as a complete population.
export async function fetchCompleteReportingCollection(endpoint, collectionKey, { signal } = {}) {
  let total = null;
  let observedAt = null;
  let page = 1;
  const records = [];
  const ids = new Set();

  do {
    const response = await axios.get(endpoint, { params: { page, limit: PAGE_SIZE }, signal });
    const payload = response.data?.data ?? response.data;
    const pageTotal = countOf(payload?.pagination?.total ?? payload?.total);
    const pageNumber = payload?.pagination?.page ?? payload?.page;
    const items = payload?.[collectionKey];
    if (pageTotal === null) {
      throw unavailable('Complete reporting requires a collection and an authoritative pagination total.');
    }
    if ((total !== null && pageTotal !== total) || (pageNumber !== undefined && pageNumber !== page)) {
      throw unavailable('The collection changed while loading. Refresh to obtain complete results.');
    }
    total = pageTotal;
    observedAt = new Date().toISOString();
    if (!Array.isArray(items)) {
      throw unavailable('Complete reporting requires a collection and an authoritative pagination total.', total, observedAt);
    }
    if (total > MAX_RECORDS) {
      throw unavailable('Setup required: this collection needs a server reporting aggregate above 50,000 records.', total, observedAt);
    }
    if (items.length > PAGE_SIZE || (items.length === 0 && records.length < total)) {
      throw unavailable('The server did not return all records promised by its pagination total.', total, observedAt);
    }
    for (const item of items) {
      const id = idOf(item);
      if (!id || ids.has(id)) {
        throw unavailable('Missing or repeated record IDs prevent a complete report. Refresh or request server aggregates.', total, observedAt);
      }
      ids.add(id);
      records.push(item);
    }
    if (records.length > total) {
      throw unavailable('The collection no longer matches its pagination total. Refresh the report.');
    }
    page += 1;
  } while (records.length < total);

  // A final count check detects additions/removals that occur after page one.
  if (page > 2) {
    const response = await axios.get(endpoint, { params: { page: 1, limit: 1 }, signal });
    const payload = response.data?.data ?? response.data;
    if (countOf(payload?.pagination?.total ?? payload?.total) !== total) {
      throw unavailable('The collection changed while loading. Refresh to obtain complete results.');
    }
    observedAt = new Date().toISOString();
  }
  return { records, total, error: null, observedAt };
}

export async function getReportingSnapshot({ signal } = {}) {
  const results = await Promise.allSettled([
    fetchCompleteReportingCollection('/users/admin/users', 'users', { signal }),
    fetchCompleteReportingCollection('/jobs/admin/jobs', 'jobs', { signal }),
    axios.get('/subscriptions/admin/stats', { signal }).then((response) => ({ response, observedAt: new Date().toISOString() })),
  ]);
  const source = (result) => result.status === 'fulfilled' ? result.value : {
    records: null,
    total: result.reason.total ?? null,
    observedAt: result.reason.observedAt ?? null,
    error: result.reason.response?.data?.message || result.reason.message || 'This data source could not be loaded.',
  };
  const subscriptionResult = results[2];
  const statsPayload = subscriptionResult.status === 'fulfilled'
    ? subscriptionResult.value.response.data?.data?.stats ?? subscriptionResult.value.response.data?.stats ?? subscriptionResult.value.response.data
    : null;

  return {
    users: source(results[0]),
    jobs: source(results[1]),
    activeSubscriptions: {
      value: countOf(statsPayload?.active),
      observedAt: subscriptionResult.status === 'fulfilled' ? subscriptionResult.value.observedAt : null,
      error: subscriptionResult.status === 'rejected'
        ? subscriptionResult.reason.response?.data?.message || 'Subscription statistics could not be loaded.'
        : countOf(statsPayload?.active) === null ? 'An authoritative active subscription count was not returned.' : null,
    },
    capturedAt: new Date().toISOString(),
  };
}

export const getReportSupportCreatedCount = async (range) => {
  let value;
  try { value = await getSupportCreatedCount(range); }
  catch (error) {
    if (error.code && error.code !== 'support/incomplete-history') throw new Error(formatSupportError(error));
    throw error;
  }
  if (countOf(value) === null) throw new Error('An authoritative support ticket count was not returned.');
  return value;
};

const metric = (value, reason = '') => ({ value: countOf(value), reason });
const BILLING_HISTORY_REASON = 'Setup required: successful payment and subscription event history is not exposed by the existing admin API.';
const validDate = (value) => typeof value === 'string'
  && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/.test(value)
  && Number.isFinite(Date.parse(value));
const dateCoverage = (records) => Array.isArray(records) && records.every((record) => validDate(record.createdAt));
const verificationStatus = (business) => {
  const explicit = business.businessVerification?.status;
  if (['approved', 'pending', 'rejected', 'not_requested'].includes(explicit)) return explicit;
  if (business.businessVerified === true || business.isBusinessDocVerified === true) return 'approved';
  return 'unknown';
};

export function buildReportingView(snapshot, range, support = {}) {
  const users = snapshot?.users?.records;
  const jobs = snapshot?.jobs?.records;
  const usersDated = dateCoverage(users);
  const jobsDated = dateCoverage(jobs);
  const usersReason = snapshot?.users?.error || 'Registration timestamps are missing from one or more retained user records.';
  const jobsReason = snapshot?.jobs?.error || 'Creation timestamps are missing from one or more retained job records.';
  const newUsers = usersDated ? users.filter((user) => isInReportingRange(user.createdAt, range)) : null;
  const periodJobs = jobsDated ? jobs.filter((job) => isInReportingRange(job.createdAt, range)) : null;
  const businessUsers = Array.isArray(users) ? users.filter((user) => user.role === 'businessOwner') : null;
  const rolesComplete = Array.isArray(users) && users.every((user) => typeof user.role === 'string' && user.role !== '');
  const businesses = businessUsers?.map((business) => ({ ...business, verificationStatus: verificationStatus(business) })) ?? [];
  const verificationComplete = rolesComplete && businesses.every((business) => business.verificationStatus !== 'unknown');
  const jobOwnersComplete = Array.isArray(jobs) && jobs.every((job) => idOf(job.businessId));
  const postedBy = new Set(Array.isArray(jobs) ? jobs.map((job) => idOf(job.businessId)).filter(Boolean) : []);
  const unpostedBusinesses = rolesComplete && jobOwnersComplete
    ? businesses.filter((business) => !postedBy.has(idOf(business))) : null;
  const needsDocuments = businesses.filter((business) => ['not_requested', 'rejected'].includes(business.verificationStatus));
  const awaitingReview = businesses.filter((business) => business.verificationStatus === 'pending');
  const roleBreakdown = newUsers && newUsers.every((user) => typeof user.role === 'string' && user.role !== '') ? [
    { name: 'Businesses', value: newUsers.filter((user) => user.role === 'businessOwner').length },
    { name: 'Practitioners', value: newUsers.filter((user) => user.role === 'practitioner').length },
    { name: 'Other roles', value: newUsers.filter((user) => !['businessOwner', 'practitioner'].includes(user.role)).length },
  ] : null;

  const buckets = new Map();
  const days = Math.round((Date.parse(`${range.endDate}T00:00:00Z`) - Date.parse(`${range.startDate}T00:00:00Z`)) / 86400000) + 1;
  const monthly = days > 62;
  const date = new Date(`${range.startDate}T00:00:00Z`);
  const finalDate = new Date(`${range.endDate}T00:00:00Z`);
  if (monthly) date.setUTCDate(1);
  while (date <= finalDate) {
    const key = date.toISOString().slice(0, monthly ? 7 : 10);
    buckets.set(key, { date: key, users: usersDated ? 0 : null, jobs: jobsDated ? 0 : null });
    if (monthly) date.setUTCMonth(date.getUTCMonth() + 1);
    else date.setUTCDate(date.getUTCDate() + 1);
  }
  const addToSeries = (records, key) => records?.forEach((record) => {
    const localDate = getDateInTimeZone(record.createdAt, range.timeZone).slice(0, monthly ? 7 : 10);
    const bucket = buckets.get(localDate);
    if (bucket) bucket[key] += 1;
  });
  addToSeries(newUsers, 'users');
  addToSeries(periodJobs, 'jobs');

  return {
    historical: {
      newUsers: metric(newUsers?.length, usersReason),
      existingUsers: metric(usersDated ? users.filter((user) => Date.parse(user.createdAt) < Date.parse(range.startAt)).length : null, usersReason),
      jobsPosted: metric(periodJobs?.length, jobsReason),
      newPaidSubscriptions: metric(null, BILLING_HISTORY_REASON),
      successfulRenewals: metric(null, BILLING_HISTORY_REASON),
      cancellationRequests: metric(null, BILLING_HISTORY_REASON),
      endedSubscriptions: metric(null, 'Setup required: actual ending events and renewal reconciliation are not available; future end dates are insufficient.'),
      supportCreated: metric(support.value, support.error || 'Support ticket creation counts are not available.'),
      supportResolved: metric(null, 'Setup required: the existing support contract has no verified resolved status or resolution timestamp.'),
    },
    current: {
      totalUsers: metric(snapshot?.users?.total, snapshot?.users?.error || 'The server did not return a user total.'),
      businesses: metric(rolesComplete ? businessUsers.length : null, 'Complete user role coverage is required.'),
      practitioners: metric(rolesComplete ? users.filter((user) => user.role === 'practitioner').length : null, 'Complete user role coverage is required.'),
      activeSubscriptions: metric(snapshot?.activeSubscriptions?.value, snapshot?.activeSubscriptions?.error || ''),
      noPaidSubscription: metric(null, 'Setup required: a verified paid entitlement is not exposed. Token based businesses are not classified as owing payment.'),
      needsDocuments: metric(verificationComplete ? needsDocuments.length : null, 'Complete business verification statuses are required; missing historical fields remain Unknown.'),
      awaitingReview: metric(verificationComplete ? awaitingReview.length : null, 'Complete business verification statuses are required; missing historical fields remain Unknown.'),
      noRetainedJobs: metric(unpostedBusinesses?.length, 'Complete job ownership and business role data are required.'),
    },
    details: { newUsers: newUsers ?? [], jobs: periodJobs ?? [], needsDocuments, awaitingReview, noRetainedJobs: unpostedBusinesses ?? [] },
    roleBreakdown,
    trend: Array.from(buckets.values()),
    trendInterval: monthly ? 'month' : 'day',
    usersAvailable: usersDated,
    jobsAvailable: jobsDated,
  };
}
